import { useMarkerVisibility } from 'hooks/map/useMarkerVisibility';
import { coordsToArrays } from 'libs/utils/3d/3dUtils';
import { AnySourceImpl } from 'mapbox-gl';
import { useContext } from 'react';
import {
    ICoordinates,
    IIconV2WorldMarker,
    getFlippedCoordinates,
    getSegmentsForDirectionMarker,
    getSourceFeaturesForIcons,
    getSourceFeaturesForIndoorStands,
    transformLineCoordinatesIntoPolygonCoordinates
} from 'react-give-me-map';
import {
    IImageV2World,
    IImageV2WorldMarker,
    IIndoorStandWorldMarker
} from 'react-give-me-map/dist/utils/world/worldTypes';
import { GeoJSONSource, ImageSource } from 'react-map-gl';
import { CtxEdit } from 'session/ctxEdit';

export const useDirectMarkerUpdates = () => {
    const ctxEdit = useContext(CtxEdit);
    const mapRef = ctxEdit.mapRef.current;

    const markerVisibility = useMarkerVisibility();

    function getSourceAndFeatures<T extends AnySourceImpl>(markerId: string) {
        return {
            source: mapRef?.getSource(markerId) as T,
            features: mapRef?.querySourceFeatures(markerId)
        };
    }

    const updateLine = (markerId: string, coordinates: ICoordinates[]) => {
        const { source, features } = getSourceAndFeatures<GeoJSONSource>(markerId);

        source.setData({
            type: 'Feature',
            properties: features?.length ? features[0].properties : {},
            geometry: {
                type: 'LineString',
                coordinates: coordsToArrays(coordinates)
            }
        });
    };

    const updateDirection = (
        markerId: string,
        coordinates: ICoordinates[],
        path: ICoordinates[]
    ) => {
        const segments = getSegmentsForDirectionMarker(coordinates, path);

        segments.forEach((segment, i) => {
            const sourceId = `${markerId}|${i}`;
            const { source, features } = getSourceAndFeatures<GeoJSONSource>(sourceId);
            source.setData({
                type: 'Feature',
                properties: features?.length ? features[0].properties : {},
                geometry: {
                    type: 'LineString',
                    coordinates: coordsToArrays(segment)
                }
            });
        });
    };

    const updateVariantIconPosition = (markerId: string, position: ICoordinates) => {
        const markers = mapRef.getMap()._markers;
        const marker = markers.find(
            (m) => m.getElement().firstElementChild.getAttribute('aria-label') === markerId
        );
        marker?.setLngLat([position.lng, position.lat]);
    };

    const updateIconLikeMarkers = (
        type: 'v2/icon' | 'indoor_stand',
        markers: (IIconV2WorldMarker | IIndoorStandWorldMarker)[]
    ) => {
        const getSourceId = () => {
            if (type === 'v2/icon') {
                return 'icons';
            }
            if (type === 'indoor_stand') {
                return 'indoor_stands';
            }
        };

        const { source } = getSourceAndFeatures<GeoJSONSource>(getSourceId());

        if (!source) {
            return;
        }

        if (type === 'v2/icon') {
            source.setData({
                type: 'FeatureCollection' as const,
                features: getSourceFeaturesForIcons(
                    (markers as IIconV2WorldMarker[]).map((e) => ({
                        ...e,
                        visible: markerVisibility.isMarkerVisibleInEdit(e)
                    }))
                )
            });
        } else {
            source.setData({
                type: 'FeatureCollection' as const,
                features: getSourceFeaturesForIndoorStands(
                    (markers as IIndoorStandWorldMarker[]).map((e) => ({
                        ...e,
                        visible: markerVisibility.isMarkerVisibleInEdit(e)
                    }))
                )
            });
        }

        source.setData;
    };

    const updateIconLikeMarker = (
        type: 'v2/icon' | 'indoor_stand',
        marker: IIconV2WorldMarker | IIndoorStandWorldMarker
    ) => {
        const markers = ctxEdit.worldMarkers
            .filter<IIconV2WorldMarker | IIndoorStandWorldMarker>(
                (e): e is IIconV2WorldMarker | IIndoorStandWorldMarker => e.elementType === type
            )
            .map((e) => (e.id === marker.id ? marker : e));

        updateIconLikeMarkers(type, markers);
    };

    const updateLineWall = (
        markerId: string,
        newCoordinates: ICoordinates[],
        width: number,
        isLineWithFloor: boolean
    ) => {
        const { source, features } = getSourceAndFeatures<GeoJSONSource>(markerId);

        const coordinates = transformLineCoordinatesIntoPolygonCoordinates(
            isLineWithFloor ? [...newCoordinates, newCoordinates[0]] : newCoordinates,
            width
        ).map(coordsToArrays);

        source.setData({
            type: 'FeatureCollection',
            features: coordinates.map((c) => ({
                type: 'Feature',
                properties: features?.length ? features[0].properties : {},
                geometry: {
                    type: 'Polygon',
                    coordinates: [c]
                }
            }))
        });

        if (isLineWithFloor) {
            updateWallFloor(markerId, newCoordinates);
        }
    };

    const setImageVisibility = (markerId: string, visibility: boolean) => {
        mapRef
            ?.getMap()
            ?.setLayoutProperty(markerId + '|layer', 'visibility', visibility ? 'visible' : 'none');
    };

    const updateImage = (markerId: string, partialData: Partial<IImageV2World>) => {
        const { source } = getSourceAndFeatures<ImageSource>(markerId);

        const originalData = ctxEdit.worldMarkers.find<IImageV2WorldMarker>(
            (e): e is IImageV2WorldMarker => e.id === markerId
        ).elementData;

        const markerData = { ...originalData, ...partialData };

        const flippedCoordinates = getFlippedCoordinates({
            flipHorizontal: markerData.flipHorizontal,
            flipVertical: markerData.flipVertical,
            coordinates: markerData.coordinates
        });
        const coordinates = coordsToArrays(flippedCoordinates);
        source.setCoordinates(coordinates);

        if (originalData.imageUrl !== markerData.imageUrl) {
            source.updateImage({
                url: markerData.imageUrl,
                coordinates
            });
        }
    };

    const addTempCircle = (coordinates: ICoordinates, color: string) => {
        mapRef.getMap().addSource('temp-circle-source', {
            type: 'geojson',
            data: {
                type: 'Feature',
                geometry: {
                    type: 'Point' as const,
                    coordinates: [coordinates.lng, coordinates.lat]
                },
                properties: {}
            }
        });
        mapRef.getMap().addLayer({
            id: 'temp-circle-layer',
            source: 'temp-circle-source',
            type: 'circle',
            paint: {
                'circle-color': color,
                'circle-radius': 7
            }
        });
    };

    const removeTempCirlce = () => {
        mapRef.getMap().removeLayer('temp-circle-layer');
        mapRef.getMap().removeSource('temp-circle-source');
    };

    const updatePolygon = (
        markerId: string,
        newCoordinates: ICoordinates[],
        type: 'wall' | 'regular',
        opts: { closePolygon: boolean; wallHeight?: number }
    ) => {
        const { source, features } = getSourceAndFeatures<GeoJSONSource>(markerId);

        if (newCoordinates.length < 3) {
            if (type === 'wall') {
                updateLineWall(markerId, newCoordinates, 5, false);
            } else {
                updateLine(markerId, newCoordinates);
            }
            return;
        }

        const coordinates = coordsToArrays(
            opts.closePolygon ? [...newCoordinates, newCoordinates[0]] : newCoordinates
        );
        source.setData({
            type: 'Feature',
            properties: features?.length ? features[0].properties : {},
            geometry: {
                type: 'Polygon',
                coordinates: [coordinates]
            }
        });
    };

    const updateWallFloor = (markerId: string, newCoordinates: ICoordinates[]) => {
        const { source, features } = getSourceAndFeatures<GeoJSONSource>(markerId + '|floor');

        const coordinates = coordsToArrays([...newCoordinates, newCoordinates[0]]);

        source.setData({
            type: 'Feature',
            properties: features?.length ? features[0].properties : {},
            geometry: {
                type: 'Polygon',
                coordinates: [coordinates]
            }
        });
    };

    const updateWallHeight = (markerId: string, height: number) => {
        mapRef?.getMap()?.setPaintProperty(markerId + '|layer', 'fill-extrusion-height', height);
    };

    return {
        updateLine,
        updateLineWall,
        updatePolygon,
        updateWallFloor,
        updateIconLikeMarker,
        updateIconLikeMarkers,
        updateImage,
        setImageVisibility,
        updateDirection,
        addTempCircle,
        removeTempCirlce,
        updateWallHeight,
        updateVariantIconPosition
    };
};
